//*Be sure to add class="gridBinder" to all Parent Divs to act as stop points allowing Nesting of Grids */
interface GridBinderOptions {
    $parent: JQuery<HTMLElement>;
    controller: string;
    searchUrl?: string;
    deleteInGridUrl?: string;
    name?: string;
    csvUrl?: string;
    excelUrl?: string;
    isFormData?: boolean;

    // Optional overrides
    bindAdditionalSearchBar?(): void;
    bindAdditionalGridButtons?(): void;
    bindAdditionalGridCollapse?(): void;
    additionalClearGridSearch?(): void;
    postFilterChange?(event: any): void;
    bindAdditionalModalButtons?(): void;

    filterKeys?: string[];
}

class GridBinder {
    static instances: GridBinder[] = [];

    //#region GridBinder Properties
    id: string;
    $parent: JQuery<HTMLElement>;
    subGridBinders: GridBinder[] = [];

    controller: string;
    searchUrl: string;
    deleteInGridUrl: string;
    name: string;
    csvUrl: string;
    excelUrl: string;
    isFormData: boolean;

    SubGridBinders: GridBinder[] = [];
    //#endregion GridBinder Properties

    //#region Search Bar Elements
    btnSearch!: JQuery<HTMLElement>;
    btnClearSearch!: JQuery<HTMLElement>;
    txtSearch!: JQuery<HTMLElement>;
    selectSearchType!: JQuery<HTMLElement>;
    btnCsv!: JQuery<HTMLElement>;
    btnCsvAll!: JQuery<HTMLElement>;
    btnExcel!: JQuery<HTMLElement>;
    btnExcelAll!: JQuery<HTMLElement>;
    divSearchText!: JQuery<HTMLElement>;
    divSearchDate!: JQuery<HTMLElement>;
    txtBeginDate!: JQuery<HTMLElement>;
    txtEndDate!: JQuery<HTMLElement>;
    btnGridModalAdd!: JQuery<HTMLElement>;
    grid!: JQuery<HTMLElement>;
    searchAdditionalProperties!: JQuery<HTMLElement>;
    //#endregion Search Bar Elements

    //#region Grid Elements
    btnGoToPage!: JQuery<HTMLElement>;
    txtGoToPage!: JQuery<HTMLElement>;
    pagingButtons!: JQuery<HTMLElement>;

    controllerName!: JQuery<HTMLElement>;
    gridType!: JQuery<HTMLElement>;
    pageType!: JQuery<HTMLElement>;
    modalFilterKey!: JQuery<HTMLElement>;
    modalFilterCustomGrid!: JQuery<HTMLElement>;
    gridAdditionalProperties!: JQuery<HTMLElement>;

    sortHeaders!: JQuery<HTMLElement>;
    sortType!: JQuery<HTMLElement>;
    sortOrderDesc!: JQuery<HTMLElement>;

    page!: JQuery<HTMLElement>;

    itemsPerPage!: JQuery<HTMLElement>;
    filterCriteria!: JQuery<HTMLElement>;

    btnDeleteInGrid!: JQuery<HTMLElement>;
    //#endregion Grid Elements

    //#region Modal Elements
    gridModalFields!: JQuery<HTMLElement>;
    btnGridModalView!: JQuery<HTMLElement>;
    btnGridModalEdit!: JQuery<HTMLElement>;
    btnGridModalSave!: JQuery<HTMLElement>;
    //#endregion Modal Elements

    //#region Optional override methods (from options)
    bindAdditionalSearchBar!: () => void;
    bindAdditionalGridButtons!: () => void;
    bindAdditionalGridCollapse!: () => void;
    additionalClearGridSearch!: () => void;
    postFilterChange!: (event?: any) => void;
    bindAdditionalModalButtons!: () => void;
    //#endregion Optional override methods (from options)

    // Filters
    filterKeys!: string[];
    filters!: Record<string, JQuery<HTMLElement>>;
    modalFilterGridBinders!: Record<string, ModalFilterGridBinder>;

    constructor(options: GridBinderOptions) {
        this.id = generateGuid();

        if (!options.$parent || options.$parent.length === 0) {
            throw new Error("GridBinder requires a valid $parent element.");
        }
        options.$parent[0].id = this.id;
        this.$parent = options.$parent;

        // Explicit property overrides for required properties
        this.controller = options.controller;

        // Set Defaults for fall back.
        this.searchUrl = 'Search';
        this.deleteInGridUrl = 'DeleteInGrid';
        this.name = '';
        this.csvUrl = 'DownloadCsv';
        this.excelUrl = 'DownloadExcel';
        this.isFormData = false;

        // Default no-op implementations
        this.bindAdditionalSearchBar = () => { };
        this.bindAdditionalGridButtons = () => { };
        this.bindAdditionalGridCollapse = () => { };
        this.additionalClearGridSearch = () => { };
        this.postFilterChange = () => { };
        this.bindAdditionalModalButtons = () => { };

        // Filters
        this.filterKeys = [];
        this.filters = {};
        this.modalFilterGridBinders = {};

        // Copy *all* option properties/functions into `this`
        Object.keys(options).forEach(key => {
            const value = (options as any)[key];
            if (value !== undefined) {
                (this as any)[key] = value;
            }
        });

        GridBinder.instances.push(this);
    }

    //#region Base Grid Functions
    bindSearchBar(): void {
        this.btnSearch = this.$parent.findInGrid('div[name="_searchBar"] button[name="btnSearch"]');
        this.btnClearSearch = this.$parent.findInGrid('div[name="_searchBar"] button[name="btnClearSearch"]');
        this.txtSearch = this.$parent.findInGrid('div[name="_searchBar"] input[name="txtSearch"]');
        this.selectSearchType = this.$parent.findInGrid('div[name="_searchBar"] select[name="selectSearchType"]');
        this.btnCsv = this.$parent.findInGrid('div[name="_searchBar"] button[name="btnCsv"]');
        this.btnCsvAll = this.$parent.findInGrid('div[name="_searchBar"] button[name="btnCsvAll"]');
        this.btnExcel = this.$parent.findInGrid('div[name="_searchBar"] button[name="btnExcel"]');
        this.btnExcelAll = this.$parent.findInGrid('div[name="_searchBar"] button[name="btnExcelAll"]');
        this.divSearchText = this.$parent.findInGrid('div[name="_searchBar"] div[name="divSearchText"]');
        this.divSearchDate = this.$parent.findInGrid('div[name="_searchBar"] div[name="divSearchDate"]');
        this.txtBeginDate = this.$parent.findInGrid('div[name="_searchBar"] input[name="txtBeginDate"]');
        this.txtEndDate = this.$parent.findInGrid('div[name="_searchBar"] input[name="txtEndDate"]');
        this.btnGridModalAdd = this.$parent.findInGrid('div[name="_searchBar"] button[name="btnGridModalAdd"]');

        this.searchAdditionalProperties = this.btnGridModalAdd.closest('.gridAddButton').findInGrid('.additional-properties').find(':input');

        this.grid = this.$parent.findInGrid('div[name="_grid"]');

        this.btnSearch.on('click', (event) => this.searchGrid(event.currentTarget as HTMLElement));
        this.btnClearSearch.on('click', () => this.clearGridSearch());
        this.txtSearch.on("keydown", (event) => this.gridSearchKeyPress(event));
        this.txtBeginDate.on("keydown", (event) => this.gridSearchKeyPress(event));
        this.txtEndDate.on("keydown", (event) => this.gridSearchKeyPress(event));
        this.btnCsv.on('click', (event) => this.downloadCsv(event.currentTarget as HTMLElement));
        this.btnCsvAll.on('click', (event) => this.downloadCsvAll(event.currentTarget as HTMLElement));
        this.btnExcel.on('click', (event) => this.downloadExcel(event.currentTarget as HTMLElement));
        this.btnExcelAll.on('click', (event) => this.downloadExcelAll(event.currentTarget as HTMLElement));
        this.selectSearchType.on('change', (event) => this.toggleSearch(event));
        this.btnGridModalAdd.on('click', (event) => this.openGridModalFields(event, PageType.Add));

        this.bindAdditionalSearchBar();
        this.bindSearchBarFilters();
        this.bindGridButtons();
        this.bindModalSelect();
        this.bindModalFilter();

        RegisterModalFilterGridBinders(this);
    }

    bindModalSelect(): void { }//Keeping so that the ModalSelectGridBinder can override to initalize it.
    bindModalFilter(): void { }//Keeping so that the ModalFilterGridBinder can override to initalize it.

    bindSearchBarFilters(): void {
        if (!this.filterKeys || !this.filterKeys.length) {
            return;
        }

        this.filters = {};

        this.filterKeys.forEach((key: string) => {
            const $select = this.$parent.findInGrid(
                `div[name="_searchBarFilters"] select[name="${key}"]`
            );

            if ($select.length) {
                this.filters[key] = $select;

                $select.on('change', (e) => {
                    this.filterChange($select[0], e);
                });
            } else {
                const $input = this.$parent.findInGrid(
                    `div[name="_searchBarFilters"] input[name="${key}"]`
                );

                if ($input.length) {
                    this.filters[key] = $input;

                    $input.on('input change', (e) => {
                        this.filterTextChange($input[0], e);
                    });

                    $input.on('keydown', (e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            this.btnSearch.click();
                        }
                    });
                }
            }
        });
    }

    gridSearchKeyPress(event: JQuery.KeyDownEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.btnSearch.click();

            const $target = $(event.target);
            if ($target.hasClass('date-mask')) {
                ($target as any).datepicker('hide'); // hide bootstrap datepicker if it has one.
            }
        }
    }

    goToPageKeyPress(event: JQuery.KeyUpEvent): void {
        const value = this.txtGoToPage.val();
        this.btnGoToPage.val(value ?? '');

        if (event.key === 'Enter') {
            event.preventDefault();
            this.btnGoToPage.click();
        }
    }

    bindGridButtons(): void {
        this.pagingButtons = this.$parent.findInGrid('div[name="_grid"] button[name="Paging.Page"]');
        this.btnGoToPage = this.$parent.findInGrid('div[name="_grid"] button[name="btnGoToPage"]');
        this.txtGoToPage = this.$parent.findInGrid('div[name="_grid"] input[name="txtGoToPage"]');

        this.controllerName = this.$parent.findInGrid('[name="_grid"] input[name="ControllerName"]');
        this.gridType = this.$parent.findInGrid('[name="_grid"] input[name="GridType"]');
        this.pageType = this.$parent.findInGrid('[name="_grid"] input[name="PageType"]');
        this.gridAdditionalProperties = this.$parent.findInGrid('[name="_grid"] [name^="AdditionalProperties"]');

        this.modalFilterKey = this.$parent.findInGrid('[name="_grid"] input[name="ModalFilterKey"]');
        this.modalFilterCustomGrid = this.$parent.findInGrid('[name="_grid"] input[name="ModalFilterCustomGrid"]');

        this.sortHeaders = this.$parent.findInGrid('div[name="_grid"] th.sortHeader');
        this.sortType = this.$parent.findInGrid('div[name="_grid"] input[name="Paging.SortType"]');
        this.sortOrderDesc = this.$parent.findInGrid('div[name="_grid"] input[name="Paging.SortOrderDesc"]');

        this.page = this.$parent.findInGrid('div[name="_grid"] input[name="Paging.Page"]');

        this.itemsPerPage = this.$parent.findInGrid('[name="_grid"] select[name="Paging.ItemsPerPage"]');
        this.filterCriteria = this.$parent.findInGrid('[name="_grid"] [name^="Paging.FilterCriteria"]');

        this.btnDeleteInGrid = this.$parent.findInGrid('div[name="_grid"] button[name="btnDeleteInGrid"]');

        this.pagingButtons.on('click', (event) => this.searchGrid(event.currentTarget as HTMLElement));
        this.btnGoToPage.on('click', (event) => this.searchGrid(event.currentTarget as HTMLElement));
        this.txtGoToPage.on('keyup', (event) => this.goToPageKeyPress(event));
        this.sortHeaders.on('click', (event) => this.sortGrid(event.currentTarget as HTMLElement));
        this.itemsPerPage.on('change', () => this.btnSearch.click());

        this.btnDeleteInGrid.on('click', (event) => this.handleDeleteInGridClick(event.currentTarget as HTMLElement));

        // Reset CSV/Excel button values for current page
        this.btnCsv.val(this.txtGoToPage.val() ?? '');
        this.btnExcel.val(this.txtGoToPage.val() ?? '');

        // Additional bindings
        this.bindGridModalFields();
        this.bindAdditionalGridButtons();
        this.bindGridCollapse();
        this.bindGridTabs();
        this.bindWindowReSize();
    }

    bindGridModalFields(): void {
        this.gridModalFields = this.$parent.findInGrid('div[name="_grid"] div[name="gridModalFields"]');

        this.btnGridModalView = this.$parent.findInGrid('div[name="_grid"] button[name="btnGridModalView"]');
        this.btnGridModalEdit = this.$parent.findInGrid('div[name="_grid"] button[name="btnGridModalEdit"]');

        this.btnGridModalSave = this.gridModalFields.findInGrid('button[name="btnGridModalSave"]');

        this.btnGridModalView.on('click', (e) => this.openGridModalFields(e, PageType.View));
        this.btnGridModalEdit.on('click', (e) => this.openGridModalFields(e, PageType.Edit));

        this.btnGridModalSave.on('click', (e) => this.saveGridModalFields(e));
    }

    bindWindowReSize(): void {
        // Run once for initial load width
        this.bindGridCollapseSize();
        this.bindGridButtonColumnSize();
    }

    bindGridCollapseSize(): void {
        this.$parent.findInGrid('.gridCollapsePanel').css('max-width', `${getHiddenElementOuterWidth(this.$parent)}px`);
    }

    bindGridButtonColumnSize(): void {
        const gridButtonColumns = this.$parent.findInGrid('.gridButtonColumn');

        gridButtonColumns.each((_, el) => {
            const gridButtonColumn = $(el);
            const buttons = gridButtonColumn.find('.btn');

            let totalWidth = 0;

            buttons.each((_, btn) => {
                totalWidth += getHiddenElementOuterWidth($(btn));
            });

            totalWidth += buttons.length * 7;

            gridButtonColumn.css('width', `${totalWidth}px`);
        });
    }

    bindGridCollapse(): void {
        // Set the max-width of gridCollapsePanels
        //bind collapse buttons to next row that is collapsable
        this.$parent.findInGrid('.collapse-button').click(function () {
            const collapseRow = $(this).closest('tr').nextAll('.collapse').first();
            collapseRow.collapse('toggle');
        });

        this.$parent.findInGrid('.collapse-exception-button').click(function () {
            const collapseRow = $(this).closest('tr').nextAll('.collapse-exception').first();
            collapseRow.collapse('toggle');
        });

        this.$parent.findInGrid('.collapse-eventdata-button').click(function () {
            const collapseRow = $(this).closest('tr').nextAll('.collapse-eventdata').first();
            collapseRow.collapse('toggle');
        });

        this.bindAdditionalGridCollapse();
    }

    bindGridTabs(): void {
        this.$parent.findInGrid('.nav-tabs .nav-item button.nav-link').click((event: any) => {
            const button = $(event.currentTarget);
            const tabPaneId = button.attr('data-bs-target');
            if (!tabPaneId) return;

            const tabParent = button.closest('div');
            const tabPane = tabParent.findInGrid(tabPaneId);

            tabParent.findInGrid('.active').removeClass('active');

            button.addClass('active');
            tabPane.addClass('active');
        });
    }

    sortGrid(element: HTMLElement): void {
        const $el = $(element);

        this.sortType.val($el.data("column"));

        if ($el.hasClass('sortActive')) {
            const current = this.sortOrderDesc.val();
            this.sortOrderDesc.val(current === "False" ? "True" : "False");
        } else {
            this.sortOrderDesc.val("False");
        }

        this.btnSearch.click();
    }

    searchGrid(element: HTMLElement): void {
        const page = $(element).val() || 1;
        const model = this.getSearchGridModel(page);

        $.ajax({
            type: "POST",
            url: `${window.appRoot}${this.controller}/${this.searchUrl}`,
            data: model,
            success: (data: AjaxSaveDetailsResult) => {
                this.loadGridData(data);
            },
            error: (_xhr, _status, error) => {
                console.log(error);
            }
        });
    }

    getSearchGridModel(page?: any): GridSearch {
        const resolvedPage = page ?? 1;

        const model: GridSearch = {
            ControllerName: this.controllerName.val() as string,
            SearchTxt: this.txtSearch.val() as string,
            SearchBeginDate: this.txtBeginDate.val() as string,
            SearchEndDate: this.txtEndDate.val() as string,
            SelectedSearchType: this.selectSearchType.length ? parseInt(this.selectSearchType.val() as string) : 0,
            GridType: GridType[this.gridType.val() as keyof typeof GridType],
            PageType: PageType[this.pageType.val() as keyof typeof PageType],
            ModalFilterKey: this.modalFilterKey.val() as string,
            ModalFilterCustomGrid: this.modalFilterCustomGrid.val() as string,

            Paging: {
                Page: Number(resolvedPage),
                ItemsPerPage: Number(this.itemsPerPage.val()),
                SortType: this.sortType.val() as string,
                SortOrderDesc: this.sortOrderDesc.val() === "True",
                FilterCriteria: []
            },

            AdditionalProperties: []
        };

        // FilterCriteria (Paging)
        const filterCriteriaArray = this.filterCriteria.serializeArray().reduce((accumulator: any[], current, index, array) => {
            if (index % 2 === 0) {
                accumulator.push({
                    Name: current.value,
                    Value: encodeURIComponent(array[index + 1]?.value ?? '')
                });
            }
            return accumulator;
        }, []);

        model.Paging.FilterCriteria = filterCriteriaArray;

        // AdditionalProperties (Grid)
        const additionalPropertiesArray = this.gridAdditionalProperties.serializeArray().reduce((accumulator: any[], current, index, array) => {
            if (index % 2 === 0) {
                accumulator.push({
                    Name: current.value,
                    Value: encodeURIComponent(array[index + 1]?.value ?? '')
                });
            }
            return accumulator;
        }, []);

        model.AdditionalProperties = additionalPropertiesArray;

        return model;
    }

    loadGridData(data: AjaxSaveDetailsResult): void {
        if (data.Html) {
            // Replace grid HTML
            this.grid.html(data.Html);

            // Re-bind common/global behaviors
            BindCommon(this.grid);

            // Re-bind grid-specific buttons/events
            this.bindGridButtons();

            // Clear old sub grids
            this.subGridBinders = [];

            // Re-register nested grid binders
            RegisterGridBinders(this.$parent);
        }

        // Replace grid Message
        if (data.SaveMessageView) {
            appendMessagePartial(this.$parent, data.SaveMessageView);
        }
    }

    clearGridSearch(): void {
        this.txtSearch.val('');
        this.txtBeginDate.val('');
        this.txtEndDate.val('');

        (this.selectSearchType as JQuery<HTMLSelectElement>).val(0).trigger('change');

        this.clearGridSearchFilters();
        this.additionalClearGridSearch();

        this.btnSearch.click();
    }

    clearGridSearchFilters(): void {
        if (!this.filterKeys.length) {
            return;
        }

        this.filterKeys.forEach((filterKey: string) => {
            const modalFilterGridBinder = this.modalFilterGridBinders[filterKey];
            if (modalFilterGridBinder) {
                modalFilterGridBinder.clearModalFilter();
                return;
            }

            const $filter = this.filters[filterKey];
            if ($filter.is('select')) {
                const value = $filter.find('option:first').val();

                $filter.val(value ?? '').trigger('change');
            } else if ($filter.is('input')) {
                $filter.val('').trigger('change');
            }
        });
    }

    toggleSearch(event: any): void {
        const selectedText = this.selectSearchType.find(':selected').text().toLowerCase();

        if (selectedText.includes('date')) {
            this.divSearchDate.show();
            this.divSearchText.hide();
        } else {
            this.divSearchDate.hide();
            this.divSearchText.show();
        }
    }

    downloadCsv(element: HTMLElement): void {
        this.downloadGridExportFile(element, this.csvUrl, 'download.csv', false);
    }

    downloadCsvAll(element: HTMLElement): void {
        this.downloadGridExportFile(element, `${this.csvUrl}All`, 'download.csv', false);
    }

    downloadExcel(element: HTMLElement): void {
        this.downloadGridExportFile(element, this.excelUrl, 'download.xlsx', true);
    }

    downloadExcelAll(element: HTMLElement): void {
        this.downloadGridExportFile(element, `${this.excelUrl}All`, 'download.xlsx', true);
    }

    private downloadGridExportFile(
        element: HTMLElement,
        url: string,
        defaultFileName: string,
        isBlob: boolean
    ): void {
        const page = $(element).val() || 1;
        const model = this.getSearchGridModel(page);

        const ajaxOptions: JQueryAjaxSettings = {
            type: 'POST',
            url: `${window.appRoot}${this.controller}/${url}`,
            data: model,
            success: (data: any, _status: string, xhr: JQuery.jqXHR) => {

                let blob: Blob;
                let objectUrl: string;

                if (isBlob) {
                    const contentType = xhr.getResponseHeader('Content-Type') || 'application/octet-stream';
                    blob = new Blob([data], { type: contentType });
                    objectUrl = window.URL.createObjectURL(blob);
                } else {
                    blob = new Blob([data]);
                    objectUrl = window.URL.createObjectURL(blob);
                }

                const link = document.createElement('a');
                link.href = objectUrl;

                const fileName = getDownloadedFileNameFromDisposition(xhr.getResponseHeader('Content-Disposition'), defaultFileName);

                link.download = fileName;

                document.body.appendChild(link);
                link.click();

                if (isBlob) {
                    window.URL.revokeObjectURL(objectUrl);
                }

                document.body.removeChild(link);
            },
            error: (_xhr, _status, error) => {
                console.log(error);
            }
        };

        if (isBlob) {
            ajaxOptions.xhrFields = { responseType: 'blob' };
        }

        $.ajax(ajaxOptions);
    }

    filterChange(element: HTMLElement, event: any): void {
        const $select = $(element);

        const filterConstant = $select.attr('name');
        const filterValue = $select.val();

        if (filterConstant) {
            this.setFilterCriteriaValue(filterConstant, (filterValue ?? '').toString());
        }

        // Only search if changed manually
        if (event.originalEvent) {
            this.btnSearch.click();
        }

        this.postFilterChange(event);
    }

    filterTextChange(element: HTMLElement, event: any): void {
        //Same as FilterChange but for TextBoxes Don't Run Search for every Change Require Button Click.
        const $input = $(element);

        const filterConstant = $input.attr('name');
        const filterValue = $input.val();

        if (filterConstant) {
            this.setFilterCriteriaValue(filterConstant, (filterValue ?? '').toString());
        }

        this.postFilterChange(event);
    }

    setFilterCriteriaValue(filterName: string, filterValue: string): void {
        const nameElement = this.filterCriteria
            .filter((_, element) => {
                const $element = $(element);
                return ($element.attr('name') ?? '').endsWith('.Name') && $element.val() === filterName;
            })
            .first();

        if (!nameElement.length) {
            return;
        }

        const index = this.getIndexFromListElement(nameElement);

        const valueName = `Paging.FilterCriteria[${index}].Value`;

        const valueElement = this.filterCriteria
            .filter((_, element) => {
                const $element = $(element);
                return $element.attr('name') === valueName;
            })
            .first();

        if (valueElement.length) {
            valueElement.val(filterValue);
        }
    }

    getIndexFromListElement(element: JQuery<HTMLElement>): string {
        const name = element.attr('name') ?? '';

        const match = name.match(/\[(\d+)\]/);

        return match ? match[1] : '';
    }

    deleteInGrid(element: HTMLElement): void {
        const $button = $(element);
        const id = $button.val();

        const page = this.page.val();
        const gridModel = this.getSearchGridModel(page);

        const $modal = $('#deleteConfirmModal');

        $.ajax({
            type: 'POST',
            url: `${window.appRoot}${this.controller}/${this.deleteInGridUrl}`,
            data: { id: id, gridModel: gridModel },
            success: (response: AjaxSaveDetailsResult): void => {
                if (response.Success) {
                    getModal($modal).hide();

                    this.loadGridData(response);
                } else {
                    appendMessagePartial($modal, response.SaveMessageView);
                }
            },
            error: (_xhr, _status, error) => {
                console.log(error);
            }
        });
    }

    handleDeleteInGridClick(element: HTMLElement): void {
        const dataHtml = this.buildDeletePreviewHtml(element);
        showDeleteConfirmModal("Are you sure you want to delete this item?", dataHtml, () => this.deleteInGrid(element));
    }

    buildDeletePreviewHtml(element: HTMLElement): string {
        const $row = $(element).closest('tr').clone();

        $row.findInGrid('td').filter(function () {
            return $(this).findInGrid('button').length > 0;
        }).remove();

        const $table = $(element).closest('table');

        const $headerRow = $table.findInGrid('thead tr').filter(function () { return $(this).find('th').length > 0; }).clone();

        $headerRow.findInGrid('th').filter(function () {
            return $(this).text().trim() === '';
        }).remove();

        $headerRow.findInGrid('th')
            .removeClass('sortHeader')
            .removeClass('sortActive')
            .findInGrid('span.asc, span.desc')
            .remove();

        return `
        <table class="table">
            <thead>${$headerRow.prop('outerHTML')}</thead>
            <tbody>${$row.prop('outerHTML')}</tbody>
        </table>
    `;
    }
    //#endregion Base Grid Functions

    //#region Grid Modal Fields
    openGridModalFields(event: any, pageType: PageType): void {
        const element = event.currentTarget as HTMLElement;
        const modelID = $(element).val();

        const model: any = {
            modelID: modelID,
            pageType: pageType,
        };

        // Append additional properties
        this.searchAdditionalProperties.each(function () {
            const input = $(this);
            const name = input.attr('name');

            if (name) {
                model[name] = input.val();
            }
        });

        $.ajax({
            type: "POST",
            url: `${window.appRoot}${this.controller}/GetGridModalFields`,
            data: model,
            success: (data: any) => {
                if (this.checkForScriptToExecute(data)) return; // stop success handler

                this.gridModalFields.findInGrid('.modal-title').html(data.Title);
                this.gridModalFields.findInGrid('.modal-body').html(data.Fields);
                this.btnGridModalSave.toggle(pageType !== PageType.View);
                $('html, body').animate({ scrollTop: 0 }, 100);
                BindCommon(this.gridModalFields.findInGrid('.modal-body'));
                this.bindAdditionalModalButtons();

                // Remove previous handler
                this.gridModalFields.off('shown.bs.modal');

                /*RegisterGridBinders needs to be in the event listener so it
                only runs code that sizes gridButtonColumns after they are showing*/
                this.gridModalFields.on('shown.bs.modal', (e: any) => {
                    // Ensure the event is triggered by this modal
                    if (e.target === this.gridModalFields[0]) {
                        RegisterGridBinders(this.$parent);
                    }
                });

                this.gridModalFields.modal('show');
            },
            error: (_xhr, _status, error) => {
                console.log(error);
                if (_xhr.status == 403) {
                    setMessageError(this.$parent, "Access Denied");
                } else {
                    setMessageError(this.$parent, error);
                }
            }
        });
    }

    saveGridModalFields(event: any, saveOverrideMethod?: string): void {
        const page = this.page.val();

        const dataModel = this.getGridModalFieldsDetails(event);
        const gridModel = this.getSearchGridModel(page);

        let model: any;

        if (this.isFormData) {
            model = dataModel;
            this.appendFormData(model, gridModel, "gridModel");
        } else {
            model = {
                dataModel: dataModel,
                gridModel: gridModel,
            };
        }

        const $modal = this.gridModalFields;

        const controllerMethod = saveOverrideMethod || 'SaveGridModalFields';

        const ajaxOptions: JQueryAjaxSettings = {
            type: "POST",
            url: `${window.appRoot}${this.controller}/${controllerMethod}`,
            data: model,
            success: (response: AjaxSaveDetailsResult): void => {
                if (response.Success) {
                    getModal($modal).hide();

                    this.loadGridData(response);
                } else {
                    appendMessagePartial($modal, response.SaveMessageView);
                    $('.modal.show').animate({ scrollTop: 0 }, 100);
                }
            },
            error: (_xhr, _status, error) => {
                console.log(error);
                if (_xhr.status == 403) {
                    setMessageError($modal, "Access Denied");
                } else {
                    setMessageError($modal, error);
                }
                $('.modal.show').animate({ scrollTop: 0 }, 100);
            }
        };

        if (this.isFormData) {
            ajaxOptions.contentType = false;
            ajaxOptions.processData = false;
        }

        $.ajax(ajaxOptions);
    }

    checkForScriptToExecute(data: any): boolean {
        const content = typeof data === "string" ? data : data?.toString?.() ?? "";

        const scriptMatch = content.match(/<script>([\s\S]*?)<\/script>/i);

        if (!scriptMatch || scriptMatch.length < 2) {
            return false;
        }

        const scriptContent = scriptMatch[1];

        try {
            eval(scriptContent);
        } catch (error) {
            console.error("Error executing server script:", error);
        }

        return true;
    }

    // Helper function to flatten nested objects
    appendFormData(formData: FormData, data: any, parentKey: string): void {
        // If data is an object, recursively append keys
        if (data && typeof data === 'object' && !(data instanceof File)) {
            Object.keys(data).forEach((key) => {
                const fullKey = parentKey ? `${parentKey}[${key}]` : key;

                this.appendFormData(formData, data[key], fullKey);
            });
        } else {
            // If it's not an object, append the value directly
            if (data !== undefined && data !== null) {
                formData.append(parentKey, data);
            }
        }
    }

    getGridModalFieldsDetails(event: any): any {
        const $modalBody = this.gridModalFields.findInGrid('div .modal-body');

        if (this.isFormData) {
            return GetFormDataUnMasked($modalBody);
        }

        return $modalBody.findInGrid(':input').not('[name^="inline"] :input').serializeObject();
    }
    //#endregion Grid Modal Fields
}

